import { ref } from 'vue'
import {
  getConfig,
  getConfigDetail,
  getConfigValue,
  saveConfigDetail,
  updateConfigDetail,
  type ConfigDetailSaveRequest,
  type ConfigVO
} from '@/api/config'

export type AutoQuoteStrategy = 'PRIORITY_LOWEST' | 'PRIORITY_OWN' | 'PRIORITY_NEWEST'

export interface AutoQuoteSettings {
  enabled: boolean
  strategy: AutoQuoteStrategy
}

const ENABLED_CONFIG_NAME = 'auto.quote.enabled'
const STRATEGY_CONFIG_NAME = 'auto.quote.strategy'
const defaultSettings: AutoQuoteSettings = {
  enabled: false,
  strategy: 'PRIORITY_LOWEST'
}

const settings = ref<AutoQuoteSettings>({ ...defaultSettings })
const loading = ref(false)
const saving = ref(false)
const loaded = ref(false)

const isStrategy = (value: string): value is AutoQuoteStrategy =>
  ['PRIORITY_LOWEST', 'PRIORITY_OWN', 'PRIORITY_NEWEST'].includes(value)

const buildSaveRequest = async(config: ConfigVO, configValue: string): Promise<ConfigDetailSaveRequest> => {
  if (!config.id || !config.configName) {
    throw new Error('Auto quote configuration definition is missing')
  }

  const detail = config.configDetail || await getConfigDetail({ configDefId: config.id })
  return {
    id: detail?.id || '',
    configDefId: config.id,
    configName: config.configName,
    configValue,
    dataType: config.dataType,
    overrideable: config.overrideable,
    literals: config.literals,
    remarks: config.remarks
  }
}

const persistConfigValue = async(configName: string, configValue: string) => {
  const config = await getConfig({ configName })
  const request = await buildSaveRequest(config, configValue)
  return request.id
    ? updateConfigDetail(request)
    : saveConfigDetail(request)
}

export function useAutoQuoteSettings() {
  const load = async(force = false) => {
    if (loaded.value && !force) return settings.value

    loading.value = true
    try {
      const [enabledValue, strategyValue] = await Promise.all([
        getConfigValue(ENABLED_CONFIG_NAME),
        getConfigValue(STRATEGY_CONFIG_NAME)
      ])
      settings.value = {
        enabled: String(enabledValue).trim().toLowerCase() === 'true',
        strategy: isStrategy(String(strategyValue).trim())
          ? String(strategyValue).trim() as AutoQuoteStrategy
          : defaultSettings.strategy
      }
      loaded.value = true
      return settings.value
    } finally {
      loading.value = false
    }
  }

  const save = async(value: AutoQuoteSettings) => {
    saving.value = true
    try {
      const results = await Promise.all([
        persistConfigValue(ENABLED_CONFIG_NAME, String(value.enabled)),
        persistConfigValue(STRATEGY_CONFIG_NAME, value.strategy)
      ])
      if (results.some(result => !result)) {
        throw new Error('Failed to save auto quote configuration')
      }
      settings.value = { ...value }
      loaded.value = true
    } finally {
      saving.value = false
    }
  }

  return { settings, loading, saving, load, save }
}
